import { motion } from "framer-motion";
/**
 * EmSwash — replaces the "italic + text-color" emphasis pattern with a
 * hand-drawn underline swash.
 *
 * Drop-in for anywhere you'd previously have written:
 *   <em className="italic text-tangerine">Pay it forward.</em>
 *
 * The text itself stays ink-colored (inherits from the parent) and
 * upright — the swash below carries the emphasis instead of the
 * italic-in-color pair. Same visual weight, less "AI slop".
 *
 * COLOR PATTERN (site rule, 2026-09-06). Swashes rotate through the
 * DoodleDivider palette in divider order, yellow excluded (it vanishes
 * on sand):
 *   1. The first swash on a page (hero / H1 masthead) is tangerine.
 *   2. Each following section heading takes the next color:
 *      cobalt → grass → blossom → tangerine → … A section with several
 *      swashed lines (the Manifesto) carries the rotation through them.
 *   3. The footer restarts at cobalt (headline) and closes on tangerine
 *      (sign-off), so every page ends on the brand color.
 *   4. Inside a toned card or dialog, the swash takes the card's tone.
 * Use `swashAt(i)` when a list needs the rotation programmatically.
 */
export const SWASH_ROTATION = ["text-tangerine", "text-cobalt", "text-grass", "text-blossom"];
export const swashAt = (i) => SWASH_ROTATION[i % SWASH_ROTATION.length];

export const EmSwash = ({
  children,
  className = "",
  color = "text-tangerine",
  draw = false,
  ...rest
}) => (
  <span
    className={`relative inline-block pb-[0.06em] ${className}`}
    {...rest}
  >
    {children}
    <svg
      className={`pointer-events-none absolute inset-x-0 -bottom-[0.02em] h-[0.14em] w-full ${color}`}
      viewBox="0 0 400 22"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {draw ? (
        <motion.path
          d="M6,14 Q60,4 130,12 T260,15 T394,10"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
        />
      ) : (
        <path
          d="M6,14 Q60,4 130,12 T260,15 T394,10"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  </span>
);
